import { isAutomatedBrowser } from "./automatedBrowser.js";
import { useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { auth as salesiveAuth } from "salesive-api-axios";
import { SalesiveTracker } from "./autoTracker.js";

/**
 * Resolve the best token we have for the socket handshake.
 *
 * Order of preference:
 * 1. Ask the store API (`auth.session()`) — this is the source of truth once
 *    the cookie model is live, and the only way to read an httpOnly cookie
 *    from the browser. The store sets a parent-domain `.salesive.com` cookie
 *    that the events service trusts, so a session there is also a session here.
 * 2. Fall back to `SALESIVE_ACCESS_TOKEN` in localStorage for back-compat with
 *    the pre-cookie world. This branch will go away once the localStorage
 *    token is no longer being written anywhere.
 * 3. `null` — connect anonymously (the socket backend will treat this as a
 *    ghost session, which is the same behavior as the old code when the
 *    token was missing).
 *
 * Synchronous callers (the initial `useEffect`) get only the localStorage
 * fallback; the REST-based session lookup is async and the socket can't wait
 * for it at construction time. We kick it off in the background and re-apply
 * the resolved token via `ensureConnected`.
 */
function readTokenSync() {
    return localStorage.getItem("SALESIVE_ACCESS_TOKEN");
}

async function readTokenAsync() {
    try {
        const res = await salesiveAuth.session();
        const token =
            res?.data?.data?.token ||
            res?.data?.token ||
            res?.data?.data?.accessToken;
        if (token) {
            // Mirror into localStorage so synchronous paths stay in sync.
            localStorage.setItem("SALESIVE_ACCESS_TOKEN", token);
            return token;
        }
    } catch {
        // Cookie missing or session endpoint unavailable — fall through.
    }
    return readTokenSync();
}

/**
 * Socket.IO hook that stores socket instance on window
 * Does NOT cause rerenders - uses refs and window storage
 *
 * @param {string} url - Socket.IO server URL
 * @param {object} options - Socket.IO client options
 * @returns {object} - { getSocket, emit, on, off, connect, disconnect }
 */
export function useSocket(url, options = {}) {
    const socketRef = useRef(null);
    const listenersRef = useRef(new Map());

    // Initialize socket (only once)
    useEffect(() => {
        // Singleton pattern: Check if socket already exists on window
        // We check existence primarily, connection state is secondary
        if (window.SalesiveSocket) {
            socketRef.current = window.SalesiveSocket;
            // If it exists but disconnected, we might want to let ensureConnected handle it
            // rather than recreating it here.
            return;
        }

        const socketUrl =
            url ||
            import.meta.env.VITE_SOCKET_URL ||
            "wss://events.salesive.com";
        const storeId =
            import.meta.env.VITE_SALESIVE_SHOP_ID ||
            localStorage.getItem("SALESIVE_SHOP_ID");

        if (!socketUrl) {
            console.warn("[SalesiveSocket] No socket URL provided");
            return;
        }

        const auth = {
            token: readTokenSync(),
            storeId: storeId || "",
        };

        // Create socket instance
        const socket = io(socketUrl, {
            // transports: ["websocket", "polling"], // try WebSocket first
            upgrade: true,
            autoConnect: false,
            reconnection: true,
            reconnectionAttempts: 9999999999,
            reconnectionDelay: 5000,
            // Send the parent-domain `jwt` cookie on the handshake so the
            // store-set cookie at .salesive.com authenticates the socket
            // without a separate token. Must be true before the connection
            // starts — setting it later is a no-op.
            withCredentials: true,
            ...options,
            auth,
        });

        // If the sync token is empty, try the cookie-backed session once and
        // re-apply it before the first connect attempt. This bridges the gap
        // where the store set a cookie that the socket needs to learn about.
        if (!auth.token) {
            readTokenAsync().then((token) => {
                if (token && socket.auth) {
                    socket.auth = { ...socket.auth, token };
                }
            });
        }

        // Store on window for external access
        window.SalesiveSocket = socket;
        socketRef.current = socket;

        // Debug logging
        socket.on("connect", () => {
            // console.log("[SalesiveSocket] Connected:", socket.id);
        });

        socket.on("disconnect", (reason) => {
            // console.log("[SalesiveSocket] Disconnected:", reason);
        });

        socket.on("connect_error", (error) => {
            console.error("[SalesiveSocket] Connection error:", error.message);
        });

        return () => {
            // We do NOT destroy the socket here because it is a global singleton on window.
            // We only cleanup local listener refs if needed, but since listenersRef is per-hook instance,
            // and the socket is global, we need to be careful.
            // The current cleanup logic removes listeners *registered by this hook instance*.
            // That is correct.
            const activeSocket = socketRef.current || window.SalesiveSocket;
            if (activeSocket) {
                listenersRef.current.forEach((handlers, event) => {
                    handlers.forEach((handler) => {
                        activeSocket.off(event, handler);
                    });
                });
            }
            listenersRef.current.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]); // Intentionally exclude 'options' to prevent re-init loop. Config changes should likely require a full reload or manual disconnect.

    // Get socket instance (no rerender)
    const getSocket = useCallback(() => {
        return socketRef.current || window.SalesiveSocket;
    }, []);

    // Emit event (no rerender)
    const emit = useCallback(
        (event, ...args) => {
            const socket = getSocket();
            if (socket?.connected) {
                socket.emit(event, ...args);
            } else {
                console.warn(
                    "[SalesiveSocket] Cannot emit, socket not connected",
                );
            }
        },
        [getSocket],
    );

    // Subscribe to event (no rerender)
    const on = useCallback(
        (event, handler) => {
            const socket = getSocket();
            if (socket && typeof handler === "function") {
                socket.on(event, handler);
                if (!listenersRef.current.has(event)) {
                    listenersRef.current.set(event, new Set());
                }
                listenersRef.current.get(event).add(handler);
            }
        },
        [getSocket],
    );

    // Unsubscribe from event (no rerender)
    const off = useCallback(
        (event, handler) => {
            const socket = getSocket();
            if (socket && typeof handler === "function") {
                socket.off(event, handler);
                const handlers = listenersRef.current.get(event);
                if (handlers) {
                    handlers.delete(handler);
                    if (handlers.size === 0) {
                        listenersRef.current.delete(event);
                    }
                }
            }
        },
        [getSocket],
    );

    const ensureConnected = useCallback(() => {
        if (isAutomatedBrowser()) {
            return Promise.reject(
                new Error("[SalesiveSocket] Not connecting from an automated browser"),
            );
        }
        const socket = getSocket();
        if (!socket) {
            return Promise.reject(
                new Error("[SalesiveSocket] Socket instance not available"),
            );
        }

        // Update auth with the latest known token before each connect attempt.
        // The async lookup hits the store API to pick up a token derived from
        // the parent-domain cookie (if any) before the handshake goes out.
        const storeId =
            import.meta.env.VITE_SALESIVE_SHOP_ID ||
            localStorage.getItem("SALESIVE_SHOP_ID");

        return readTokenAsync().then((token) => {
            socket.auth = { token, storeId: storeId || "" };

            if (socket.connected) {
                return socket;
            }

            return new Promise((resolve, reject) => {
                const cleanup = () => {
                    socket.off("connect", handleConnect);
                    socket.off("connect_error", handleError);
                };

                const handleConnect = () => {
                    cleanup();
                    resolve(socket);
                };

                const handleError = (error) => {
                    cleanup();
                    reject(error);
                };

                socket.once("connect", handleConnect);
                socket.once("connect_error", handleError);

                // console.log("[SalesiveSocket] Attempting to connect...");

                if (!socket.active) {
                    socket.connect();
                } else if (!socket.connecting) {
                    socket.connect();
                }
            });
        });
    }, [getSocket]);

    // Connect socket (no rerender)
    const connect = useCallback(() => {
        ensureConnected().catch((error) => {
            console.error("[SalesiveSocket] Failed to connect", error);
        });
    }, [ensureConnected]);

    // Disconnect socket (no rerender)
    const disconnect = useCallback(() => {
        const socket = getSocket();
        if (socket?.connected) {
            socket.disconnect();
        }
    }, [getSocket]);

    // ---- Customer Messaging Helpers ----
    const joinConversation = useCallback(
        (payload = {}, callback) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("message:join", payload, callback);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] joinConversation failed",
                        err,
                    );
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const markConversationRead = useCallback(
        (conversationId, callback) => {
            ensureConnected()
                .then((socket) => {
                    if (!conversationId) {
                        throw new Error("conversationId is required");
                    }
                    socket.emit("message:read", { conversationId }, callback);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] markConversationRead failed",
                        err,
                    );
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const fetchHistory = useCallback(
        (options = {}, callback) => {
            ensureConnected()
                .then((socket) => {
                    const payload = {
                        limit: Math.min(Math.max(options.limit ?? 50, 1), 100),
                        ...(options.conversationId
                            ? { conversationId: options.conversationId }
                            : {}),
                        ...(options.customerId
                            ? { customerId: options.customerId }
                            : {}),
                    };
                    socket.emit("message:history", payload, callback);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] fetchHistory failed", err);
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const sendCustomerMessage = useCallback(
        (message, callback) => {
            ensureConnected()
                .then((socket) => {
                    const payload = {
                        sender: "customer",
                        type: "text",
                        ...message,
                    };
                    console.log(
                        "[SalesiveSocket] Emitting message:send",
                        payload,
                    );
                    socket.emit("message:send", payload, callback);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] sendCustomerMessage failed",
                        err,
                    );
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    // ---- Store & User Events ----
    const joinStore = useCallback(
        (storeId, callback) => {
            ensureConnected()
                .then((socket) => {
                    if (!storeId) throw new Error("storeId is required");
                    socket.emit("store:join", { storeId }, callback);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] joinStore failed", err);
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const leaveStore = useCallback(
        (storeId, callback) => {
            ensureConnected()
                .then((socket) => {
                    if (!storeId) throw new Error("storeId is required");
                    socket.emit("store:leave", { storeId }, callback);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] leaveStore failed", err);
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const sendUserEvent = useCallback(
        (eventData, callback) => {
            ensureConnected()
                .then((socket) => {
                    const payload = {
                        timestamp: new Date().toISOString(),
                        ...eventData,
                    };
                    socket.emit("user:event", payload, callback);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] sendUserEvent failed", err);
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    // ---- Event Listeners ----

    const onMessageNew = useCallback(
        (handler) => {
            if (typeof handler !== "function") {
                console.warn(
                    "[SalesiveSocket] onMessageNew: handler is not a function",
                );
                return () => {};
            }
            on("message:new", handler);
            return () => off("message:new", handler);
        },
        [on, off],
    );

    const onMessageSent = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:sent", handler);
            return () => off("message:sent", handler);
        },
        [on, off],
    );

    const onMessageHistory = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:history", handler);
            return () => off("message:history", handler);
        },
        [on, off],
    );

    const onMessageRead = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:read", handler);
            return () => off("message:read", handler);
        },
        [on, off],
    );

    const onMessageError = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:error", handler);
            return () => off("message:error", handler);
        },
        [on, off],
    );

    const onMessageJoined = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:joined", handler);
            return () => off("message:joined", handler);
        },
        [on, off],
    );

    const onStoreJoined = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("store:joined", handler);
            return () => off("store:joined", handler);
        },
        [on, off],
    );

    const onStoreLeft = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("store:left", handler);
            return () => off("store:left", handler);
        },
        [on, off],
    );

    const onStoreMessageNew = useCallback(
        (handler) => {
            if (typeof handler !== "function") {
                console.warn(
                    "[SalesiveSocket] onStoreMessageNew: handler is not a function",
                );
                return () => {};
            }
            on("store:message:new", handler);
            return () => off("store:message:new", handler);
        },
        [on, off],
    );

    const onStoreEventNew = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("store:event:new", handler);
            return () => off("store:event:new", handler);
        },
        [on, off],
    );

    const startTyping = useCallback(
        (conversationId, options = {}) => {
            ensureConnected()
                .then((socket) => {
                    const payload = {
                        conversationId,
                        ...options,
                    };
                    socket.emit("message:typing", payload);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] startTyping failed", err);
                });
        },
        [ensureConnected],
    );

    const stopTyping = useCallback(
        (conversationId) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("message:stopped-typing", { conversationId });
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] stopTyping failed", err);
                });
        },
        [ensureConnected],
    );

    const onTypingStart = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:typing", handler);
            return () => off("message:typing", handler);
        },
        [on, off],
    );

    const onTypingStop = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("message:stopped-typing", handler);
            return () => off("message:stopped-typing", handler);
        },
        [on, off],
    );

    const onUserOnline = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("user:online", handler);
            return () => off("user:online", handler);
        },
        [on, off],
    );

    const onUserOffline = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("user:offline", handler);
            return () => off("user:offline", handler);
        },
        [on, off],
    );

    // ---- Voice Call Signaling ----

    const emitCallOffer = useCallback(
        (payload = {}, callback) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:offer", payload, callback);
                })
                .catch((err) => {
                    console.error("[SalesiveSocket] emitCallOffer failed", err);
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const emitCallAnswer = useCallback(
        (payload = {}, callback) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:answer", payload, callback);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] emitCallAnswer failed",
                        err,
                    );
                    if (callback)
                        callback({ success: false, message: err.message });
                });
        },
        [ensureConnected],
    );

    const emitCallIceCandidate = useCallback(
        (payload = {}) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:ice-candidate", payload);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] emitCallIceCandidate failed",
                        err,
                    );
                });
        },
        [ensureConnected],
    );

    const emitCallReject = useCallback(
        (payload = {}) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:reject", payload);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] emitCallReject failed",
                        err,
                    );
                });
        },
        [ensureConnected],
    );

    const emitCallHangup = useCallback(
        (payload = {}) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:hangup", payload);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] emitCallHangup failed",
                        err,
                    );
                });
        },
        [ensureConnected],
    );

    const emitCallCancel = useCallback(
        (payload = {}) => {
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:cancel", payload);
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] emitCallCancel failed",
                        err,
                    );
                });
        },
        [ensureConnected],
    );

    /**
     * Fetch STUN/TURN config (with ephemeral credentials) over the socket.
     * Resolves with a STUN-only fallback after ~5s or on any failure so a
     * call attempt can always proceed (possibly without TURN relay).
     */
    const requestIceConfig = useCallback(() => {
        const FALLBACK_ICE = {
            iceServers: [{ urls: "stun:turn.salesive.com:3478" }],
            ttl: 0,
        };
        return new Promise((resolve) => {
            let settled = false;
            const settle = (value) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(value);
            };
            const timer = setTimeout(() => {
                console.warn(
                    "[SalesiveSocket] call:ice-config timed out, using STUN fallback",
                );
                settle(FALLBACK_ICE);
            }, 5000);
            ensureConnected()
                .then((socket) => {
                    socket.emit("call:ice-config", {}, (response) => {
                        if (
                            response?.success &&
                            Array.isArray(response.iceServers) &&
                            response.iceServers.length > 0
                        ) {
                            settle({
                                iceServers: response.iceServers,
                                ttl: response.ttl,
                            });
                        } else {
                            settle(FALLBACK_ICE);
                        }
                    });
                })
                .catch((err) => {
                    console.error(
                        "[SalesiveSocket] requestIceConfig failed",
                        err,
                    );
                    settle(FALLBACK_ICE);
                });
        });
    }, [ensureConnected]);

    const onCallIncoming = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:incoming", handler);
            return () => off("call:incoming", handler);
        },
        [on, off],
    );

    const onCallAnswered = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:answered", handler);
            return () => off("call:answered", handler);
        },
        [on, off],
    );

    const onCallIceCandidate = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:ice-candidate", handler);
            return () => off("call:ice-candidate", handler);
        },
        [on, off],
    );

    const onCallRejected = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:rejected", handler);
            return () => off("call:rejected", handler);
        },
        [on, off],
    );

    const onCallCancelled = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:cancelled", handler);
            return () => off("call:cancelled", handler);
        },
        [on, off],
    );

    const onCallEnded = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:ended", handler);
            return () => off("call:ended", handler);
        },
        [on, off],
    );

    const onCallBusy = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:busy", handler);
            return () => off("call:busy", handler);
        },
        [on, off],
    );

    const onCallUnavailable = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:unavailable", handler);
            return () => off("call:unavailable", handler);
        },
        [on, off],
    );

    const onCallError = useCallback(
        (handler) => {
            if (typeof handler !== "function") return () => {};
            on("call:error", handler);
            return () => off("call:error", handler);
        },
        [on, off],
    );

    return {
        getSocket,
        emit,
        on,
        off,
        connect,
        ensureConnected,
        disconnect,
        // Methods
        joinConversation,
        fetchHistory,
        sendCustomerMessage,
        markConversationRead,
        joinStore,
        leaveStore,
        sendUserEvent,
        // Events
        onMessageNew,
        onMessageSent,
        onMessageHistory,
        onMessageRead,
        onMessageError,
        onMessageJoined,
        onStoreJoined,
        onStoreLeft,
        onStoreMessageNew,
        onStoreEventNew,
        startTyping,
        stopTyping,
        onTypingStart,
        onTypingStop,
        onUserOnline,
        onUserOffline,
        // Voice calls
        emitCallOffer,
        emitCallAnswer,
        emitCallIceCandidate,
        emitCallReject,
        emitCallHangup,
        emitCallCancel,
        requestIceConfig,
        onCallIncoming,
        onCallAnswered,
        onCallIceCandidate,
        onCallRejected,
        onCallCancelled,
        onCallEnded,
        onCallBusy,
        onCallUnavailable,
        onCallError,
    };
}

// ─── Auto-Tracker Hook ──────────────────────────────────────────────────────

/**
 * React hook that wires auto-tracking through the existing socket connection.
 * Events are sent via ensureConnected — the same authenticated path used by
 * chat, messaging, and store events. Session/identity comes from the JWT.
 *
 * Call once at the app root alongside useSocket:
 *
 *   const socket = useSocket();
 *   useAutoTracker(socket);
 *
 * @param {object} socketApi - Return value of useSocket() (needs getSocket + ensureConnected)
 * @param {object} [config] - SalesiveTracker config overrides
 * @returns {{ tracker: SalesiveTracker|null, start: () => void, stop: () => void }}
 */
export function useAutoTracker(socketApi = {}, config = {}) {
    const trackerRef = useRef(null);
    // Stable ref so the tracker always calls the latest ensureConnected/getSocket
    const socketApiRef = useRef(socketApi);
    socketApiRef.current = socketApi;

    useEffect(() => {
        const tracker = new SalesiveTracker(
            {
                getSocket: () => socketApiRef.current.getSocket?.(),
            },
            config,
        );
        trackerRef.current = tracker;
        tracker.start();

        return () => {
            tracker.stop();
            trackerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount

    const start = useCallback(() => {
        trackerRef.current?.start();
    }, []);

    const stop = useCallback(() => {
        trackerRef.current?.stop();
    }, []);

    return { tracker: trackerRef.current, start, stop };
}

/**
 * Get socket instance directly (outside React)
 * @returns {Socket|null}
 */
export function getSocketInstance() {
    return window.SalesiveSocket || null;
}

export default useSocket;
